import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Badge } from "@shared/components/ui/badge";
import { Textarea } from "@shared/components/ui/textarea";
import { Separator } from "@shared/components/ui/separator";
import {
  CheckCircle2,
  AlertTriangle,
  Package,
  User,
  DollarSign,
  Edit3,
  Save,
  X,
} from "lucide-react";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import ImportedPackageActions from "./ImportedPackageActions";
import { ErrorDetailsCard } from "./ImportErrorComponents";

export function EnhancedPackageImportedCard({
  initialOrder,
  hasError,
  onFixError,
}) {
  const { t } = useTranslation();
  const [order, setOrder] = useState(initialOrder);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setOrder(initialOrder);
    setHasChanges(false);
  }, [initialOrder]);

  const handleInputChange = (field, value) => {
    setOrder((prevOrder) => {
      const updateNestedField = (obj, keys, value) => {
        if (keys.length === 1) {
          return {
            ...obj,
            [keys[0]]: value,
          };
        }
        const [key, ...restKeys] = keys;

        if (key.match(/^\[\d+\]$/)) {
          const index = parseInt(key.slice(1, -1), 10);
          const array = Array.isArray(obj) ? obj : [];
          const updatedArray = [...array];
          updatedArray[index] = updateNestedField(
            array?.[index] || {},
            restKeys,
            value
          );
          return updatedArray;
        }

        return {
          ...obj,
          [key]: updateNestedField(obj?.[key] || {}, restKeys, value),
        };
      };

      const keys = field.split(".");
      const newOrder = {
        ...prevOrder,
        data: updateNestedField(prevOrder?.data || {}, keys, value),
      };

      setHasChanges(true);
      return newOrder;
    });
  };

  const handleSave = () => {
    setIsEditing(false);
    setHasChanges(false);
    // Here you would typically save the changes to the backend
  };

  const handleCancel = () => {
    setOrder(initialOrder);
    setIsEditing(false);
    setHasChanges(false);
  };

  const getFieldError = (fieldName) => {
    return order?.errors?.find((error) =>
      error?.toLowerCase()?.includes(fieldName?.toLowerCase())
    );
  };

  const hasFieldError = (fieldName) => {
    return hasError(order, fieldName);
  };

  const isValid = order?.errors?.length === 0;

  return (
    <Card
      className={`w-full transition-all duration-200 ${
        !isValid
          ? "border-red-200 bg-red-50/30 dark:bg-red-950/10"
          : "border-green-200 bg-green-50/30 dark:bg-green-950/10"
      }`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isValid ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800"
                >
                  {t("import.valid")}
                </Badge>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <Badge variant="destructive">
                  {order?.errors?.length}{" "}
                  {order?.errors?.length === 1
                    ? t("import.error")
                    : t("import.errors")}
                </Badge>
              </div>
            )}

            <div className="text-sm text-muted-foreground">
              ID: {order?._id}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasChanges && (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} className="h-8">
                  <Save className="h-3 w-3 mr-1" />
                  {t("common.save")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="h-8"
                >
                  <X className="h-3 w-3 mr-1" />
                  {t("common.cancel")}
                </Button>
              </div>
            )}

            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(!isEditing)}
              className="h-8"
            >
              <Edit3 className="h-3 w-3 mr-1" />
              {isEditing ? t("import.viewMode") : t("import.editMode")}
            </Button>

            <ImportedPackageActions id={order?._id} packageData={order} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error Display */}
        {!isValid && (
          <ErrorDetailsCard
            errors={order?.errors}
            packageId={order?._id}
            onFixError={onFixError}
          />
        )}

        {!isValid && <Separator />}

        {/* Package Information */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Customer Information */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <User className="h-4 w-4" />
              {t("import.customerInformation")}
            </div>

            <div className="space-y-2">
              <div>
                <Label className="text-xs">{t("import.customerName")}</Label>
                {isEditing ? (
                  <Input
                    value={order?.data?.customer?.name}
                    onChange={(e) =>
                      handleInputChange("customer.name", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("customer.name") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.customerName")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("customer.name")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.customer?.name || t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("customer.name") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("customer.name")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("import.phone")}</Label>
                {isEditing ? (
                  <Input
                    value={order?.data?.customer?.phone}
                    onChange={(e) =>
                      handleInputChange("customer.phone", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("customer.phone") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.phone")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("customer.phone")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.customer?.phone || t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("customer.phone") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("customer.phone")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("import.address")}</Label>
                {isEditing ? (
                  <Textarea
                    value={order?.data?.customer?.address}
                    onChange={(e) =>
                      handleInputChange("customer.address", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("customer.address") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.customerAddress")}
                    rows={2}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("customer.address")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.customer?.address || t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("customer.address") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("customer.address")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("import.city")}</Label>
                {isEditing ? (
                  <div className="mt-1">
                    <SelectCity
                      city={order?.data?.customer?.city}
                      onSelectCity={(city) =>
                        handleInputChange("customer.city", city?.name)
                      }
                    />
                  </div>
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("city")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.customer?.city || t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("city") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("city")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Package className="h-4 w-4" />
              {t("import.packageDetails")}
            </div>

            <div className="space-y-2">
              <div>
                <Label className="text-xs">{t("import.trackingNumber")}</Label>
                {isEditing ? (
                  <Input
                    value={order?.data?.trackingNumber}
                    onChange={(e) =>
                      handleInputChange("trackingNumber", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("trackingNumber") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.trackingNumber")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border font-mono ${
                      hasFieldError("trackingNumber")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.trackingNumber || t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("trackingNumber") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("trackingNumber")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("import.productName")}</Label>
                {isEditing ? (
                  <Input
                    value={order?.data?.products?.[0]?.name || ""}
                    onChange={(e) =>
                      handleInputChange("products.[0].name", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("products[0].name") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.productName")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("products[0].name")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.products?.[0]?.name ||
                      t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("products[0].name") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("products[0].name")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("import.productQuantity")}</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={order?.data?.products?.[0]?.quantity || ""}
                    onChange={(e) =>
                      handleInputChange("products.[0].quantity", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("products.quantity") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.productQuantity")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("products.quantity")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.products?.[0]?.quantity ||
                      t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("products.quantity") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("products.quantity")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              {t("import.financialInfo")}
            </div>

            <div className="space-y-2">
              <div>
                <Label className="text-xs">{t("import.totalAmount")}</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={order?.data?.totalAmount}
                    onChange={(e) =>
                      handleInputChange("totalAmount", e.target.value)
                    }
                    className={`mt-1 ${
                      hasFieldError("totalAmount") ? "border-red-500" : ""
                    }`}
                    placeholder={t("import.amount")}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border font-semibold ${
                      hasFieldError("totalAmount")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.totalAmount
                      ? `${order?.data?.totalAmount} ${t("common.currency")}`
                      : t("import.notProvided")}
                  </div>
                )}
                {hasFieldError("totalAmount") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("totalAmount")}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t("common.note")}</Label>
                {isEditing ? (
                  <Textarea
                    value={order?.data?.note}
                    onChange={(e) => handleInputChange("note", e.target.value)}
                    className={`mt-1 ${
                      hasFieldError("note") ? "border-red-500" : ""
                    }`}
                    placeholder={t("common.note")}
                    rows={3}
                  />
                ) : (
                  <div
                    className={`p-2 text-sm rounded border ${
                      hasFieldError("note")
                        ? "border-red-500 bg-red-50"
                        : "border-muted"
                    }`}
                  >
                    {order?.data?.note || t("import.noNote")}
                  </div>
                )}
                {hasFieldError("note") && (
                  <p className="text-xs text-red-500 mt-1">
                    {getFieldError("note")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
